'use client';

import { useEffect } from 'react';
import { datadogLogs } from '@datadog/browser-logs';

let isUrlMonitoringInitialized = false;

/**
 * URL Parameter Monitor Initialization Component
 *
 * Monitors URL parameter changes on the debit-credit-2 campaign page and logs
 * them to DataDog. Runs after DatadogRumInit to ensure DataDog is ready for
 * immediate logging, but still before React hydration to catch early URL changes.
 *
 * Similar to DatadogRumInit, this component uses useEffect to initialize
 * the monitoring system on the client side.
 */
export function UrlParameterMonitorInit() {
  useEffect(() => {
    // Only run on the specific campaign page and if not already initialized
    if (!window.location.pathname.includes('join/campaigns/debit-credit-2') || isUrlMonitoringInitialized) {
      return;
    }

    console.warn('[URL_PARAMS_MONITOR] Starting initialization');

    // Utility functions
    function parseUrlParams(url: string): Record<string, string> {
      try {
        const urlObj = new URL(url, window.location.origin);
        const params: Record<string, string> = {};
        urlObj.searchParams.forEach((value, key) => {
          params[key] = value;
        });
        return params;
      } catch {
        return {};
      }
    }

    function compareParams(prev: Record<string, string>, current: Record<string, string>) {
      const prevKeys = Object.keys(prev);
      const currentKeys = Object.keys(current);

      const removed = prevKeys.filter(key => !(key in current));
      const added = currentKeys.filter(key => !(key in prev));

      return { removed, added };
    }

    function getStackTrace(): string {
      try {
        throw new Error();
      } catch (e) {
        return (e as Error).stack?.split('\n').slice(2, 10).join('\n') || '';
      }
    }

    // Initialize monitoring state
    let previousUrl = window.location.href;
    let previousParams = parseUrlParams(previousUrl);
    let isMonitoring = false;

    // Log function for DataDog
    function logUrlParameterChange(data: Record<string, unknown>) {
      const logData = {
        message: `URL parameters changed on ${window.location.pathname}`,
        level: 'info',
        ...data,
        page: window.location.pathname,
      };

      // Send directly to DataDog Logs
      datadogLogs.logger.info('URL_PARAMS_CHANGED', logData);
      console.warn('[URL_PARAMS_MONITOR]', logData);
    }

    // Check for URL parameter changes
    function checkForChanges(eventType: string) {
      const currentUrl = window.location.href;
      const currentParams = parseUrlParams(currentUrl);

      const { removed, added } = compareParams(previousParams, currentParams);

      // Only log if there are actual parameter changes
      if (removed.length > 0 || added.length > 0) {
        const changeData = {
          event: eventType,
          timestamp: new Date().toISOString(),
          previousUrl: previousUrl,
          newUrl: currentUrl,
          previousParams: previousParams,
          newParams: currentParams,
          removedParams: removed,
          addedParams: added,
          stackTrace: getStackTrace(),
          userAgent: navigator.userAgent,
        };

        logUrlParameterChange(changeData);
      }

      // Update state for next comparison
      previousUrl = currentUrl;
      previousParams = currentParams;
    }

    // Store original methods
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    // Start monitoring function
    function startMonitoring() {
      if (isMonitoring) return;
      isMonitoring = true;

      console.warn('[URL_PARAMS_MONITOR] Starting monitoring for:', window.location.pathname);
      console.warn('[URL_PARAMS_MONITOR] Initial params:', previousParams);

      // Override History API
      // Mirrors the DOM History signature, whose state is any structured-cloneable value.
      // oxlint-disable-next-line anti-slop/no-unknown-parameters
      history.pushState = (state: unknown, title: string, url?: string | URL | null) => {
        const result = originalPushState.call(history, state, title, url);
        setTimeout(() => checkForChanges('history.pushState'), 0);
        return result;
      };

      // Mirrors the DOM History signature, whose state is any structured-cloneable value.
      // oxlint-disable-next-line anti-slop/no-unknown-parameters
      history.replaceState = (state: unknown, title: string, url?: string | URL | null) => {
        const result = originalReplaceState.call(history, state, title, url);
        setTimeout(() => checkForChanges('history.replaceState'), 0);
        return result;
      };

      // Listen for popstate events
      const handlePopstate = () => {
        setTimeout(() => checkForChanges('popstate'), 0);
      };
      window.addEventListener('popstate', handlePopstate);

      // Monitor anchor clicks
      const handleClick = (event: Event) => {
        const anchor = (event.target as HTMLElement).closest('a');
        if (anchor && anchor.href) {
          try {
            const targetUrl = new URL(anchor.href);
            if (targetUrl.origin === window.location.origin) {
              setTimeout(() => checkForChanges('anchor click navigation'), 10);
            }
          } catch {
            // Invalid URL, ignore
          }
        }
      };
      document.addEventListener('click', handleClick, true);

      // Monitor form submissions
      const handleSubmit = (event: Event) => {
        const form = event.target as HTMLFormElement;
        if (form && form.action) {
          try {
            const actionUrl = new URL(form.action, window.location.href);
            if (actionUrl.origin === window.location.origin) {
              setTimeout(() => checkForChanges('form submission navigation'), 10);
            }
          } catch {
            // Invalid action URL, ignore
          }
        }
      };
      document.addEventListener('submit', handleSubmit, true);

      // Cleanup function
      return () => {
        history.pushState = originalPushState;
        history.replaceState = originalReplaceState;
        window.removeEventListener('popstate', handlePopstate);
        document.removeEventListener('click', handleClick, true);
        document.removeEventListener('submit', handleSubmit, true);
      };
    }

    // Start monitoring
    const cleanup = startMonitoring();
    isUrlMonitoringInitialized = true;

    console.warn('[URL_PARAMS_MONITOR] Monitoring initialized successfully');

    // Return cleanup function
    return cleanup;
  }, []);

  return null;
}
